import { useEditor } from "@craftjs/core";
import { useLayer } from "@craftjs/layers";
import { Box, BoxProps, styled } from "@mui/material";
import { ReactNode } from "react";
import LayerHeader from "./components/LayerHeader/LayerHeader";

interface ContainerProps extends BoxProps {
    expanded: boolean;
    hasCanvases: boolean;
    hovered: boolean;
}

const Container = styled(Box, {
    shouldForwardProp: (prop) => prop !== "expanded" && prop !== "hasCanvases" && prop !== "hovered",
})<ContainerProps>(({ expanded, hasCanvases, hovered }) => ({
    background: hovered ? "#f1f1f1" : "transparent",
    display: "block",
    paddingBottom: hasCanvases && expanded ? 5 : 0,
}));

const Children = styled(Box, {
    shouldForwardProp: (prop) => prop !== "hasCanvases",
})<Pick<ContainerProps, "hasCanvases">>(({ hasCanvases, theme }) => ({
    margin: theme.spacing(0, 0, 0, hasCanvases ? 4 : 0),
    background: hasCanvases ? "rgba(255, 255, 255, 0.02)" : "transparent",
    position: "relative",
    ...(hasCanvases
        ? {
              boxShadow: "0px 0px 44px -1px #00000014",
              borderRadius: 10,
              marginRight: 5,
              marginBottom: 5,
              marginTop: 5,
              "> *": {
                  overflow: "hidden",
              },
              "&:before": {
                  position: "absolute",
                  left: -17,
                  width: 2,
                  height: "100%",
                  content: "''",
                  background: "#00000012",
              },
          }
        : {}),
}));

interface LayerNodeProps {
    children: ReactNode;
}

const LayerNode = ({ children }: LayerNodeProps) => {
    const {
        id,
        expanded,
        hovered,
        connectors: { layer },
    } = useLayer((layer) => ({
        hovered: layer.event.hovered,
        expanded: layer.expanded,
    }));
    const { hasChildCanvases } = useEditor((_, query) => {
        return {
            hasChildCanvases: query.node(id).isParentOfTopLevelNodes(),
        };
    });

    return (
        <Container ref={layer} expanded={expanded} hasCanvases={hasChildCanvases} hovered={hovered}>
            <LayerHeader />
            {children ? (
                <Children hasCanvases={hasChildCanvases} className="craft-layer-children">
                    {children}
                </Children>
            ) : null}
        </Container>
    );
};

export default LayerNode;
